import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import { loadModule } from 'utils/ModuleUtils';
export const API_URL_BASE = process.env.TCC_API_URL;

/* While working on the following ticket, we found that some sites were removing the notification banner wrapper
   which breaks our notification banner. Before we try to inject the React component, we need to make sure the
   wrapper exists. This function adds a div to the DOM */
const injectNotificationWrapper = () => {
    const wrapper = document.createElement('div');
    wrapper.setAttribute('id', 'clxNotificationWrapper');
    wrapper.setAttribute('style', 'position:absolute; top:0; left:0; right:0;');
    document.body.insertAdjacentElement('beforeend', wrapper);
};

export const injectNotificationBanner = (companyId, module, notificationProps) => {
    try {
        /* Inject the notification wrapper */
        injectNotificationWrapper();

        const script = document.createElement('script');
        script.src = module.url;
        script.type = 'text/javascript';
        script.setAttribute('data-testid', `clx-${module.name}`);
        script.async = true;

        script.onload = () => {
            const NotificationBanner = lazy(loadModule(module.name, './Module'));

            /* In case a site is removing the notification wrapper, create it again */
            const NotificationWrapper = document.querySelector('#clxNotificationWrapper');

            if (!NotificationWrapper) {
                injectNotificationWrapper();
            }

            ReactDOM.render(
                <Suspense fallback="">
                    <NotificationBanner {...notificationProps} />
                </Suspense>,
                document.querySelector('#clxNotificationWrapper')
            );
        };

        script.onerror = e => {
            process.env.NODE_ENV !== 'development'
                ? console.log(`Notification banner load failure`)
                : console.log(`Notification banner load failure - ${module.url}`);
        };

        document.body.appendChild(script);
    } catch (e) {
        process.env.NODE_ENV !== 'development'
            ? console.log('Notification banner exception')
            : console.log(e);
    }
};

export const get = async (endpoint, token) => {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    };

    const request = await fetch(`${API_URL_BASE}/${endpoint}`, options);
    const result = await request.json();
    const { status } = request;

    return {
        result,
        status
    };
};

export const loadPublicAppData = async widgetConfiguration => {
    const publicApps = sessionStorage.getItem(process.env.TCC_SESSION_STORAGE_PUBLIC_APPS);
    if (publicApps && widgetConfiguration.company_id === JSON.parse(publicApps).company_id) {
        return JSON.parse(publicApps);
    }

    const options = {
        method: 'POST',
        headers: {
            'Content-type': 'Application/json',
            Authorization: `Bearer ${widgetConfiguration.token}`
        },
        // added location so that it can get current path and show offers against that path
        body: JSON.stringify({
            api_key: process.env.WIDGET_API_KEY,
            company_id: widgetConfiguration.company_id,
            pathname: window.location.pathname
        })
    };

    const publicAppsRequest = await fetch(`${process.env.TCC_API_URL}/public_apps_view`, options);
    const publicAppsJson = await publicAppsRequest.json();
    sessionStorage.setItem(process.env.TCC_SESSION_STORAGE_PUBLIC_APPS, JSON.stringify(publicAppsJson));

    return publicAppsJson;
};

export const prepareModules = async (companyId, modules, shouldShowNotification, notificationProps) => {
    let preparedModules = new Set();
    let notificationBannerConfig = undefined;

    if (process.env.NODE_ENV === 'development') {
        const config = await import('./local.config');

        if (!config.default.modules.length) {
            console.error('There are no modules configured locally');
            return;
        }

        notificationBannerConfig = config.default.modules.filter(
            config => config.name === 'MODULE_NOTIFICATIONBANNER'
        )[0];

        modules.map(module => {
            const existingModule = config.default.modules.filter(config => config.name === module)[0];
            if (!existingModule) {
                console.warn(`No local configuration found for module: ${module}`);
                return;
            }
            preparedModules.add(existingModule);
        });
    } else {
        notificationBannerConfig = {
            name: 'MODULE_NOTIFICATIONBANNER',
            url: `${process.env.MODULE_BASE_URL}/MODULE_NOTIFICATIONBANNER/remoteEntry.js`,
            module: './Module'
        };

        modules.map(module => {
            preparedModules.add({
                name: module,
                url: `${process.env.MODULE_BASE_URL}/${module}/remoteEntry.js`,
                module: './Module'
            });
        });
    }

    if (shouldShowNotification) {
        injectNotificationBanner(companyId, notificationBannerConfig, notificationProps);
    }

    return [...preparedModules];
};

export const initiateWidgetConfigListener = setData => {
    window.addEventListener(
        'message',
        event => {
            // NOTE: this expects the event message to include an object with the following structure:
            // { name: 'updateWidgetConfig', payload: widgetConfiguration }
            if (event.data?.name === 'updateWidgetConfig' && event.data?.payload) {
                setData(event.data.payload);
            }
        },
        false
    );
};
